import { Component, ChangeDetectionStrategy, OnDestroy, ViewEncapsulation, Self } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { sortBy } from 'lodash';
import { Subject } from '../../subjects/subject';
import { Site } from '../../sites';
import { SubjectTab } from '../../subjects/subject-tab';
import { UrlBuilder } from '../../core';
import { SelectedSubjectLabResultsStorage } from './selected-subject-lab-results-storage';
import { SubjectsStorage } from '../../subjects/subjects-storage';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { GridLegendItem } from '../../legend';
import { DataRevisionIndicator } from '../../shared';
import { FormControl } from '@angular/forms';
import { SUBJECT_LAB_TESTS_DATA_SOURCE } from '../../subjects/subject-lab-tests-data-source';
import { routeData } from '../../shared/route-helpers';

interface SubjectLookupItem {
    value: string;
    subject: Subject;
}

@Component({
    selector: 'subjects-navigation',
    template: require('./subjects-navigation.component.html'),
    styles: [require('./subjects-navigation.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        SelectedSubjectLabResultsStorage,
        { provide: SUBJECT_LAB_TESTS_DATA_SOURCE, useExisting: SelectedSubjectLabResultsStorage }
    ]
})
export class SubjectsNavigationComponent implements OnDestroy {
    public subjects: Subject[];

    public selectedSubject: Subject;

    public subjectDataLegend: GridLegendItem[];

    public tabs: SubjectTab[] = [];

    public showOnlyFlagged = new FormControl();

    public selectedSubjects: SubjectLookupItem[];
    
    private subjectsSubscription: Subscription;
    private selectedSite: Site;
    private showOnlyFlaggedSubscription: Subscription;
    private subjectLookupItems: SubjectLookupItem[];
    
    constructor(
        private urlBuilder: UrlBuilder,
        route: ActivatedRoute,
        @Self() selectedSubjectLabResultsStorage: SelectedSubjectLabResultsStorage,
        private dataRevisionIndicator: DataRevisionIndicator,
        private subjectsStorage: SubjectsStorage
    ) {
        const site = route.parent.parent.data
            .pipe(map(x => x[routeData.selectedSite] as Site));

        const subjects = route.parent.data
            .pipe(map(x =>  x['subjects'] as Subject[]));

        const subject = route.data
            .pipe(map(x => x['selectedSubject'] as Subject));

        this.subjectsSubscription = combineLatest(
            site,
            subjects,
            subject
        ).subscribe((latest: [Site, Subject[], Subject]) => {
            [this.selectedSite, this.subjects, this.selectedSubject] = latest;
            this.subjects = sortBy(this.subjects, s => this.getSubjectTitle(s));

            this.selectedSubjects = this.selectedSubject ? this.getSubjectLookupItems().filter(s => s.subject.zcSubjectId === this.selectedSubject.zcSubjectId) : [];
            
            this.subjectsStorage.lastVisitedZcSubjectId = this.selectedSubject.zcSubjectId;
            this.createLegend();
            this.createTabs();
        });

        this.showOnlyFlaggedSubscription = this.showOnlyFlagged.valueChanges.subscribe((x: boolean) => {
            selectedSubjectLabResultsStorage.showOnlyFlagged.next(x);
        });
    }

    public ngOnDestroy(): void {
        this.subjectsSubscription.unsubscribe();
        this.showOnlyFlaggedSubscription.unsubscribe();
    }

    public getSubjectLookupItems() {
        if (!this.subjectLookupItems) {
            this.subjectLookupItems = this.subjects.map(s => this.getSubjectLookupItem(s));
        }
        
        return this.subjectLookupItems;
    }

    public onSelectionChanged($event: SubjectLookupItem[]) {
        this.selectedSubject = $event.length > 0 ? $event[0].subject : null;
        this.navigateToSubject();
    }

    public getSubjectTitle(subject: Subject): string {
        return SubjectTitleRenderer.renderTitle({
            data: {
                subjectNumber: subject.subjectNumber,
                initials: subject.initials,
                screeningNumber: subject.screeningNumber,
                zcSubjectId: subject.zcSubjectId,
            }
        });
    }

    private navigateToSubject() {
        this.urlBuilder
            .esa()
            .site(this.selectedSite)
            .subject(this.selectedSubject)
            .navigate({ preserveFragments: true });
    }

    private getSubjectLookupItem(subject: Subject): SubjectLookupItem {
        return {
            subject: subject,
            value: this.getSubjectTitle(subject)
        };
    }

    private createTabs() {
        this.tabs = [
            { title: 'Table', url: this.createTabUrl('lab-tests-grid') },
            { title: 'Chart', url: this.createTabUrl('lab-tests-chart') }
        ];
    }

    private createTabUrl(path: 'lab-tests-grid' | 'lab-tests-chart'): any[] {
        return this.urlBuilder
            .esa()
            .site(this.selectedSite)
            .subject(this.selectedSubject, path)
            .url({ preserveFragments: true });
    }

    private createLegend() {
        if (!this.selectedSubject) {
            return;
        }

        this.subjectDataLegend = [];

        if (this.selectedSubject.hasSubjectDataRevision) {
            this.subjectDataLegend.push({
                text: 'Δ', cssClass: 'revisioned-result', label: this.dataRevisionIndicator.subjectDataRevisionMessage
            });
        }
    }
}
